import { supabase } from "./client";
import { isNative, getAuthRedirectUrl } from "../lib/platform";

type AuthResult = { error: { message: string } | null };

const GOOGLE_WEB_CLIENT_ID = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined;
// Hardcoded iOS OAuth client ID — also appears in ios/App/App/Info.plist (GIDClientID) and
// in the reversed-client URL scheme. Not a secret; safe to commit.
const GOOGLE_IOS_CLIENT_ID = "411124001317-vp29ric2pnfr0jadibcvgboh1p5120or.apps.googleusercontent.com";
const APPLE_BUNDLE_ID = "com.weightplanner.app";

let socialLoginInitialized = false;

async function generateRawNonce(): Promise<string> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function ensureSocialLoginInitialized() {
  if (socialLoginInitialized) return;
  if (!GOOGLE_WEB_CLIENT_ID) {
    console.error(
      "[socialAuth] VITE_GOOGLE_WEB_CLIENT_ID is not set. Native Google sign-in will fail. " +
        "Set it to the Web OAuth client ID from Google Cloud Console (used so the returned ID token's `aud` matches what Supabase verifies).",
    );
  }
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  await SocialLogin.initialize({
    google: {
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
      webClientId: GOOGLE_WEB_CLIENT_ID,
      mode: "online",
    },
    apple: {
      clientId: APPLE_BUNDLE_ID,
      redirectUrl: "",
    },
  });
  socialLoginInitialized = true;
}

export async function initializeSocialAuth(): Promise<void> {
  if (!isNative) return;
  try {
    await ensureSocialLoginInitialized();
  } catch (err) {
    console.error("[socialAuth] Failed to initialize SocialLogin", err);
  }
}

export async function signInWithGoogle(): Promise<AuthResult> {
  if (!isNative) {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getAuthRedirectUrl(),
        queryParams: { prompt: "select_account" },
      },
    });
    return { error: error ? { message: error.message } : null };
  }

  if (!GOOGLE_WEB_CLIENT_ID) {
    return {
      error: {
        message:
          "Google sign-in is not configured. VITE_GOOGLE_WEB_CLIENT_ID is missing from the build. See docs/social-auth.md.",
      },
    };
  }

  try {
    await ensureSocialLoginInitialized();
    const { SocialLogin } = await import("@capgo/capacitor-social-login");
    console.info("[socialAuth][google] SocialLogin.login starting");
    const response = await SocialLogin.login({
      provider: "google",
      options: { scopes: ["profile", "email"], forcePrompt: true },
    });
    if (response.provider !== "google" || response.result.responseType !== "online") {
      return { error: { message: "Unexpected response from Google sign-in." } };
    }
    const idToken = response.result.idToken;
    console.info("[socialAuth][google] SocialLogin.login resolved", { hasIdToken: !!idToken });
    if (!idToken) return { error: { message: "Google did not return an ID token." } };

    const { error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: idToken,
    });
    console.info("[socialAuth][google] signInWithIdToken resolved", {
      errorMessage: error?.message ?? null,
    });
    if (error && /unacceptable audience|audience.*not.*authorized/i.test(error.message)) {
      return {
        error: {
          message:
            "Google sign-in is not fully configured in Supabase. The iOS OAuth client ID must be added to Authorized Client IDs in the Google provider. See docs/social-auth.md.",
        },
      };
    }
    return { error: error ? { message: error.message } : null };
  } catch (err: any) {
    if (err?.code === "USER_CANCELLED") return { error: null };
    console.error("[socialAuth][google] native sign-in threw", {
      name: err?.name,
      message: err?.message,
      code: err?.code,
    });
    return { error: { message: err?.message ?? "Google sign-in failed." } };
  }
}

export async function signInWithApple(): Promise<AuthResult> {
  if (!isNative) {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: getAuthRedirectUrl() },
    });
    return { error: error ? { message: error.message } : null };
  }

  try {
    await ensureSocialLoginInitialized();
    const { SocialLogin } = await import("@capgo/capacitor-social-login");
    // Supabase verifies the token's `nonce` claim against SHA-256(rawNonce). Apple
    // copies the value we pass into the token's nonce claim verbatim — so we send
    // the SHA-256 hash to Apple, and the raw nonce to Supabase.
    const rawNonce = await generateRawNonce();
    const hashedNonce = await sha256Hex(rawNonce);

    const response = await SocialLogin.login({
      provider: "apple",
      options: { scopes: ["name", "email"], nonce: hashedNonce },
    });

    if (response.provider !== "apple") {
      return { error: { message: "Unexpected response from Apple sign-in." } };
    }
    const idToken = response.result.idToken;
    if (!idToken) return { error: { message: "Apple did not return an identity token." } };

    const { error } = await supabase.auth.signInWithIdToken({
      provider: "apple",
      token: idToken,
      nonce: rawNonce,
    });
    if (error && /unacceptable audience|audience.*not.*authorized/i.test(error.message)) {
      return {
        error: {
          message:
            "Apple Sign-In is not fully configured in Supabase. The iOS bundle ID (com.weightplanner.app) must be added to Authorized Client IDs in the Apple provider. See docs/social-auth.md.",
        },
      };
    }
    return { error: error ? { message: error.message } : null };
  } catch (err: any) {
    if (err?.code === "USER_CANCELLED") return { error: null };
    console.error("[socialAuth][apple] native sign-in threw", {
      name: err?.name,
      message: err?.message,
      code: err?.code,
    });
    return { error: { message: err?.message ?? "Apple sign-in failed." } };
  }
}
